import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateExpense,
  useDeleteExpense,
  useExpensesInRange,
  useUpdateExpense,
} from "../../../hooks/expense";
import { useCategoriesQuery } from "../../../hooks/category";
import { useSearchParams } from "react-router-dom";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
  Category as CategoryIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import Expense from "../../../models/finance/Expense";
import {
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getMonthIndex,
} from "../../../utils/DateUtil";
import ExpenseFormDrawer from "../ExpenseFormDrawer";
import { CategoryType } from "../../../models/finance/CategoryType";
import { useCurrencyFormatter } from "../../../contexts/CurrencyContext";
import PrimaryButton from "../../common/ui/PrimaryButton";
import { useError } from "../../../error/useError";
import ErrorAlert from "../../common/ErrorAlert";
import { useUserProfileContext } from "../../../contexts/ProfileContext";

interface ExpenseTableComponentProps {
  year: number;
  month: string;
  onExpenseChange?: () => void;
  addEntryTrigger?: number;
}

const ExpenseTableComponent: React.FC<ExpenseTableComponentProps> = ({
  year,
  month,
  onExpenseChange,
  addEntryTrigger,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const { formatCurrencyNumber: formatCurrency } = useCurrencyFormatter();
  const { formatDate } = useUserProfileContext();
  const { error, helpText, clearError } = useError();
  const accent = theme.palette.primary.main;
  const accentSoft = alpha(theme.palette.primary.main, 0.22);
  const accentMuted = alpha(theme.palette.primary.main, 0.12);

  const startDate = getFirstDayOfMonth(year, getMonthIndex(month));
  const endDate = getLastDayOfMonth(year, getMonthIndex(month));

  const {
    data: expenses = [],
    isLoading: loading,
    refetch: refetchExpenses,
  } = useExpensesInRange(startDate, endDate);
  const { data: categories = [] } = useCategoriesQuery(CategoryType.Expense, true);
  const { mutate: deleteExpense, isPending: isDeletingExpense } = useDeleteExpense();
  const { mutate: createExpense } = useCreateExpense();
  const { mutate: updateExpense } = useUpdateExpense();

  const [searchParams, setSearchParams] = useSearchParams();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    (searchParams.get("sortOrder") as "asc" | "desc") || "desc"
  );
  const [sortBy, setSortBy] = useState<"date" | "amount" | "category">(
    (searchParams.get("sortBy") as "date" | "amount" | "category") || "date"
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(
    searchParams.get("category") || ""
  );
  const [searchText, setSearchText] = useState(searchParams.get("query") || "");
  const [amountBand, setAmountBand] = useState<"all" | "aboveAvg" | "topQuartile">(
    (searchParams.get("amountBand") as "all" | "aboveAvg" | "topQuartile") || "all"
  );

  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState<null | HTMLElement>(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"add" | "edit">("add");
  const [selectedExpense, setSelectedExpense] = useState<Expense | undefined>(undefined);
  const [deleteCandidate, setDeleteCandidate] = useState<Expense | undefined>(undefined);
  const [deleteOrigin, setDeleteOrigin] = useState<"row" | "drawer">("row");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const lastAddTriggerRef = useRef<number | undefined>(addEntryTrigger);

  const updateSearchParams = (newParams: Record<string, string>) => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams);
  };

  const setSort = (property: "date" | "amount" | "category") => {
    const newOrder =
      sortBy === property
        ? sortOrder === "asc"
          ? "desc"
          : "asc"
        : property === "category"
          ? "asc"
          : "desc";

    setSortBy(property);
    setSortOrder(newOrder);
    updateSearchParams({ sortBy: property, sortOrder: newOrder });
  };

  const toggleSortOrder = () => {
    const next = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(next);
    updateSearchParams({ sortBy, sortOrder: next });
  };

  const handleCategoryFilter = (categoryTitle: string) => {
    setSelectedCategory(categoryTitle);
    setCategoryMenuAnchor(null);
    updateSearchParams({ category: categoryTitle });
  };

  const clearCategoryFilter = () => {
    setSelectedCategory("");
    setCategoryMenuAnchor(null);
    updateSearchParams({ category: "" });
  };

  useEffect(() => {
    setPage(0);
  }, [selectedCategory, searchText, sortBy, sortOrder]);

  const filteredExpenses = useMemo(() => {
    const normalizedQuery = searchText.trim().toLowerCase();
    const average = expenses.length > 0 ? expenses.reduce((sum, e) => sum + e.amount, 0) / expenses.length : 0;
    const sortedAmounts = expenses.map((e) => e.amount).sort((a, b) => a - b);
    const quartileIndex = Math.floor(sortedAmounts.length * 0.75);
    const topQuartileThreshold = sortedAmounts[quartileIndex] ?? 0;

    return expenses.filter((expense) => {
      const matchesCategory = selectedCategory
        ? expense.category === selectedCategory
        : true;

      const matchesText = normalizedQuery
        ? expense.description.toLowerCase().includes(normalizedQuery) ||
          expense.category.toLowerCase().includes(normalizedQuery)
        : true;

      const matchesAmountBand =
        amountBand === "all"
          ? true
          : amountBand === "aboveAvg"
            ? expense.amount >= average && average > 0
            : expense.amount >= topQuartileThreshold && topQuartileThreshold > 0;

      return matchesCategory && matchesText && matchesAmountBand;
    });
  }, [expenses, searchText, selectedCategory, amountBand]);

  const sortedExpenses = useMemo(() => {
    return [...filteredExpenses].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "date":
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case "amount":
          comparison = a.amount - b.amount;
          break;
        case "category":
          comparison = a.category.localeCompare(b.category);
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [filteredExpenses, sortBy, sortOrder]);

  const paginatedExpenses = sortedExpenses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalAmount = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  const averageAmount = filteredExpenses.length > 0 ? totalAmount / filteredExpenses.length : 0;

  const categorySpend = useMemo(() => {
    const map = filteredExpenses.reduce<Record<string, number>>((acc, expense) => {
      acc[expense.category] = (acc[expense.category] || 0) + expense.amount;
      return acc;
    }, {});

    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
  }, [filteredExpenses]);

  const handleEditExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const handleAddExpense = () => {
    setSelectedExpense(undefined);
    setDrawerMode("add");
    setDrawerOpen(true);
  };

  useEffect(() => {
    if (addEntryTrigger === undefined) {
      return;
    }

    if (lastAddTriggerRef.current === addEntryTrigger) {
      return;
    }

    lastAddTriggerRef.current = addEntryTrigger;
    handleAddExpense();
  }, [addEntryTrigger]);

  const handleSaveExpense = (
    id: string,
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string
  ) => {
    if (drawerMode === "add") {
      createExpense(
        { date, amount, description, categoryTitle },
        {
          onSuccess: () => {
            setDrawerOpen(false);
            onExpenseChange?.();
          },
        }
      );
      return;
    }

    updateExpense(
      { id, date, amount, description, categoryTitle },
      {
        onSuccess: () => {
          setDrawerOpen(false);
          onExpenseChange?.();
        },
      }
    );
  };

  const handleSaveAndAddAnother = (
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string,
    onSuccessCallback?: () => void
  ) => {
    if (drawerMode !== "add") {
      return;
    }

    createExpense(
      { date, amount, description, categoryTitle },
      {
        onSuccess: () => {
          setDrawerOpen(true);
          onSuccessCallback?.();
        },
      }
    );
  };

  const handleRequestDeleteExpense = (expense: Expense, origin: "row" | "drawer" = "row") => {
    setDeleteCandidate(expense);
    setDeleteOrigin(origin);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteExpense = () => {
    if (!deleteCandidate) {
      return;
    }

    deleteExpense(
      { id: deleteCandidate.id },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setDeleteCandidate(undefined);
          setDrawerOpen(false);
          onExpenseChange?.();
        },
      }
    );
  };

  const handleCancelDeleteDialog = () => {
    if (isDeletingExpense) {
      return;
    }
    setDeleteDialogOpen(false);
    setDeleteCandidate(undefined);
  };

  const handleExportCsv = () => {
    const escapeCsv = (value: string | number) =>
      `"${String(value).replace(/"/g, '""')}"`;

    const rows = sortedExpenses.map((expense) => [
      formatDate(expense.date),
      expense.description,
      expense.category,
      expense.amount,
    ]);

    const csv = [
      ["Date", "Description", "Category", "Amount"],
      ...rows,
    ]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `expenses-${month}-${year}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Skeleton variant="rounded" height={84} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={56} sx={{ mb: 1.5 }} />
        {[...Array(4)].map((_, idx) => (
          <Skeleton key={idx} variant="rounded" height={74} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorAlert
          error={error}
          helpText={helpText}
          onClear={clearError}
          onRetry={() => refetchExpenses()}
          title="Expense Table Error"
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderTopLeftRadius: "18px",
          borderTopRightRadius: "18px",
          background: `linear-gradient(165deg, ${accentMuted}, transparent)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, fontFamily: '"Space Grotesk", "Manrope", sans-serif', mb: 0.5 }}
            >
              Expense Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {month} {year} ledger view with quick search, filters, and sorting.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Showing {sortedExpenses.length} of {expenses.length} transactions
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
              sx={{
                borderRadius: "10px",
                borderColor: alpha(accent, 0.45),
                color: theme.palette.primary.light,
                textTransform: "none",
                "&:hover": {
                  borderColor: accent,
                  backgroundColor: accentMuted,
                },
              }}
            >
              Export CSV
            </Button>
            <PrimaryButton startIcon={<AddIcon />} onClick={handleAddExpense}>
              Add Expense
            </PrimaryButton>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip icon={<ReceiptIcon />} label={`${filteredExpenses.length} records`} sx={{ borderRadius: "10px" }} />
          <Chip
            label={`Total ${formatCurrency(totalAmount)}`}
            sx={{
              borderRadius: "10px",
              backgroundColor: alpha(theme.palette.error.main, 0.18),
              color: theme.palette.error.light,
              fontWeight: 700,
            }}
          />
          <Chip
            label={`Average ${formatCurrency(averageAmount)}`}
            sx={{
              borderRadius: "10px",
              backgroundColor: accentSoft,
              color: theme.palette.primary.light,
            }}
          />
        </Stack>

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
          <Chip
            label="All Amounts"
            onClick={() => {
              setAmountBand("all");
              updateSearchParams({ amountBand: "all" });
            }}
            variant={amountBand === "all" ? "filled" : "outlined"}
            sx={{ borderRadius: "10px" }}
          />
          <Chip
            label="Above Average"
            onClick={() => {
              setAmountBand("aboveAvg");
              updateSearchParams({ amountBand: "aboveAvg" });
            }}
            variant={amountBand === "aboveAvg" ? "filled" : "outlined"}
            sx={{ borderRadius: "10px" }}
          />
          <Chip
            label="Top Quartile"
            onClick={() => {
              setAmountBand("topQuartile");
              updateSearchParams({ amountBand: "topQuartile" });
            }}
            variant={amountBand === "topQuartile" ? "filled" : "outlined"}
            sx={{ borderRadius: "10px" }}
          />

          {(searchText || selectedCategory || amountBand !== "all") && (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setSearchText("");
                setSelectedCategory("");
                setAmountBand("all");
                updateSearchParams({ query: "", category: "", amountBand: "all" });
              }}
              sx={{ textTransform: "none", borderRadius: "10px", color: theme.palette.text.secondary }}
            >
              Clear all controls
            </Button>
          )}
        </Stack>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ mb: 1.25 }}>
          <TextField
            value={searchText}
            onChange={(event) => {
              const value = event.target.value;
              setSearchText(value);
              updateSearchParams({ query: value });
            }}
            size="small"
            fullWidth
            placeholder="Search description or category"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(event) => setCategoryMenuAnchor(event.currentTarget)}
            sx={{
              borderRadius: "10px",
              borderColor: alpha(accent, 0.55),
              color: theme.palette.primary.light,
              minWidth: { lg: 230 },
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: accent,
                backgroundColor: accentMuted,
              },
            }}
          >
            {selectedCategory ? `Category: ${selectedCategory}` : "Filter by Category"}
          </Button>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {(["date", "amount", "category"] as const).map((field) => (
              <Chip
                key={field}
                onClick={() => setSort(field)}
                icon={<SortIcon sx={{ fontSize: 16 }} />}
                label={`Sort ${field}`}
                variant={sortBy === field ? "filled" : "outlined"}
                sx={{
                  borderRadius: "10px",
                  textTransform: "capitalize",
                  borderColor: alpha(accent, 0.5),
                  backgroundColor:
                    sortBy === field ? accentSoft : "transparent",
                }}
              />
            ))}
          </Stack>

          <Button
            variant="text"
            onClick={toggleSortOrder}
            startIcon={sortOrder === "asc" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
            sx={{
              color: theme.palette.text.secondary,
              borderRadius: "10px",
              textTransform: "none",
              alignSelf: { xs: "flex-start", md: "center" },
            }}
          >
            {sortOrder === "asc" ? "Ascending" : "Descending"}
          </Button>
        </Stack>

        {categorySpend.length > 0 && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
            {categorySpend.map(([category, amount]) => (
              <Chip
                key={category}
                onClick={() => handleCategoryFilter(category)}
                icon={<CategoryIcon />}
                label={`${category} ${formatCurrency(amount)}`}
                variant={selectedCategory === category ? "filled" : "outlined"}
                sx={{
                  borderRadius: "10px",
                  maxWidth: "100%",
                  borderColor: alpha(accent, 0.35),
                  backgroundColor:
                    selectedCategory === category
                      ? accentSoft
                      : "transparent",
                }}
              />
            ))}
          </Stack>
        )}
      </Box>

      {filteredExpenses.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2,
              bgcolor: alpha(theme.palette.error.main, 0.2),
              color: theme.palette.error.light,
            }}
          >
            <ReceiptIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            No matching expenses
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {selectedCategory || searchText
              ? "Adjust search or filters to reveal transactions."
              : `No expenses have been recorded for ${month} ${year}.`}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="center">
            <PrimaryButton startIcon={<AddIcon />} onClick={handleAddExpense}>
              Add First Expense
            </PrimaryButton>
            {(selectedCategory || searchText) && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchText("");
                  clearCategoryFilter();
                  updateSearchParams({ query: "" });
                }}
                sx={{
                  borderRadius: "10px",
                  borderColor: alpha(accent, 0.5),
                  color: theme.palette.primary.light,
                }}
              >
                Clear Filters
              </Button>
            )}
          </Stack>
        </Box>
      ) : (
        <>
          <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: { xs: 1, md: 1.5 }, pb: { xs: 4, md: 1.5 } }}>
            {!isSmall && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr 170px 150px 110px",
                  gap: 1,
                  px: 1,
                  pb: 0.75,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Date
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Description
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Category
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textAlign: "right" }}>
                  Amount
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textAlign: "center" }}>
                  Actions
                </Typography>
              </Box>
            )}

            <Stack spacing={1}>
              {paginatedExpenses.map((expense) => (
                <Box
                  key={expense.id}
                  sx={{
                    p: { xs: 1.5, md: 1.25 },
                    borderRadius: "12px",
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                    transition: "all .2s ease",
                    "&:hover": {
                      borderColor: alpha(accent, 0.45),
                      backgroundColor: accentMuted,
                    },
                  }}
                >
                  {isSmall ? (
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(expense.date)}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.error.light, fontWeight: 700 }}>
                          {formatCurrency(expense.amount)}
                        </Typography>
                      </Stack>

                      <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                        {expense.description}
                      </Typography>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Chip size="small" label={expense.category} variant="outlined" sx={{ borderRadius: 2 }} />
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Edit expense">
                            <IconButton size="small" onClick={() => handleEditExpense(expense)} sx={{ color: theme.palette.info.main }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete expense">
                            <IconButton size="small" onClick={() => handleRequestDeleteExpense(expense, "row")} sx={{ color: theme.palette.error.main }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "140px 1fr 170px 150px 110px",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(expense.date)}
                      </Typography>

                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {expense.description}
                      </Typography>

                      <Chip
                        size="small"
                        label={expense.category}
                        variant="outlined"
                        sx={{ width: "fit-content", borderRadius: 2 }}
                      />

                      <Typography
                        variant="body2"
                        sx={{ textAlign: "right", color: theme.palette.error.light, fontWeight: 700 }}
                      >
                        {formatCurrency(expense.amount)}
                      </Typography>

                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit expense">
                          <IconButton size="small" onClick={() => handleEditExpense(expense)} sx={{ color: theme.palette.info.main }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete expense">
                          <IconButton size="small" onClick={() => handleRequestDeleteExpense(expense, "row")} sx={{ color: theme.palette.error.main }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>

          <TablePagination
            component="div"
            count={sortedExpenses.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              px: { xs: 1, md: 2 },
            }}
          />
        </>
      )}

      <Menu
        anchorEl={categoryMenuAnchor}
        open={Boolean(categoryMenuAnchor)}
        onClose={() => setCategoryMenuAnchor(null)}
        PaperProps={{ sx: { maxHeight: 320, minWidth: 220 } }}
      >
        <MenuItem onClick={clearCategoryFilter}>
          <Typography sx={{ fontStyle: "italic" }}>All Categories</Typography>
        </MenuItem>
        {categories.map((category) => (
          <MenuItem
            key={category.id}
            onClick={() => handleCategoryFilter(category.title)}
            selected={selectedCategory === category.title}
          >
            {category.title}
          </MenuItem>
        ))}
      </Menu>

      <ExpenseFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveExpense}
        onSaveAndAdd={handleSaveAndAddAnother}
        onDelete={(expense) => handleRequestDeleteExpense(expense, "drawer")}
        mode={drawerMode}
        initialData={selectedExpense}
        selectedYear={year}
        selectedMonth={getMonthIndex(month)}
        categories={categories}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDeleteDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
            background: `linear-gradient(165deg, ${alpha(theme.palette.background.paper, 0.98)}, ${alpha(theme.palette.background.default, 0.98)})`,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha(theme.palette.error.main, 0.2),
                color: theme.palette.error.light,
              }}
            >
              <WarningAmberRoundedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Delete expense?
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {deleteOrigin === "drawer" ? "You are deleting from the edit panel." : "You are deleting from the transactions list."}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            This action cannot be undone. The selected expense will be permanently removed from your records.
          </Typography>
          {deleteCandidate && (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              <Chip size="small" label={formatDate(deleteCandidate.date)} />
              <Chip size="small" label={deleteCandidate.category} />
              <Chip
                size="small"
                label={formatCurrency(deleteCandidate.amount)}
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.18),
                  color: theme.palette.error.light,
                }}
              />
            </Stack>
          )}
          {deleteCandidate && (
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
              {deleteCandidate.description}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.25, pt: 0.25 }}>
          <Button
            onClick={handleCancelDeleteDialog}
            disabled={isDeletingExpense}
            sx={{ textTransform: "none" }}
          >
            Keep expense
          </Button>
          <Button
            onClick={handleConfirmDeleteExpense}
            color="error"
            variant="contained"
            disabled={isDeletingExpense || !deleteCandidate}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            {isDeletingExpense ? "Deleting..." : "Delete permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseTableComponent;
